// Copyright © 2024 CATTLEytics Inc.

import { ChangeEvent, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import Button from '../../common/components/Button';
import ImageUploaded from '../../common/components/ImageUploaded';
import ImageUploading from '../../common/components/ImageUploading';
import LightBoxModal from '../../common/components/LightBoxModal';
import { SignedUrlType } from '../../common/enums';
import { useFileService } from '../../common/hooks/useFileService';
import { getEnv, IconCreate, IconDelete } from '../../common/utilities';
import { ImageSize, resizeImage } from '../../common/utilities/imageResize';
import { ApiResourceV1, ImageUrl } from '../../shared';

interface UploadFileFieldProps {
  handleFailedUpload: (error: Error | unknown) => void;
  handleRemove: (key: string) => void;
  handleSuccessfulUpload: (imageUrl: ImageUrl) => void;
  uploadLabel: string;
  value: ImageUrl[];
}

export function UploadFileField({
  value,
  handleSuccessfulUpload,
  handleFailedUpload,
  handleRemove,
  uploadLabel,
}: UploadFileFieldProps): JSX.Element {
  const { t } = useTranslation();

  const fileService = useFileService();

  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lightBoxImageUrl, setLightBoxImageUrl] = useState<string>('');

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = ((event.target as HTMLInputElement).files as FileList)[0];
    if (!file) return;

    setBusy(true);
    try {
      const resizedImage = await resizeImage(file, ImageSize.Large);
      const fileResult = await fileService.upload(resizedImage, SignedUrlType.Original);

      if (fileResult.signedUrl) {
        handleSuccessfulUpload({
          key: fileResult.key,
          signedUrl: fileResult.signedUrl,
        });
      }
    } catch (error) {
      handleFailedUpload(error);
    }
    setBusy(false);
    setShow(false);
  };

  return (
    <div className="mt-3">
      {show && (
        <div className={'mb-1'}>
          <Form.Control name={'imageUrl'} onChange={handleFileUpload} type="file" />
          <Form.Text className="text-muted">{uploadLabel}</Form.Text>
        </div>
      )}
      <div className={'d-flex flex-wrap justify-content-start gap-2'}>
        {value.map(({ key, signedUrl }) => {
          const url =
            signedUrl || `${getEnv('API_ENDPOINT2')}${ApiResourceV1.Files}/${key ?? ''}?download=1`;
          return (
            <div key={key}>
              <div className={'text-center'}>
                <ImageUploaded
                  alt={t('taskModal|imageLabel')}
                  onClick={(): void => {
                    setLightBoxImageUrl(url);
                  }}
                  src={url}
                  style={{ cursor: 'pointer' }}
                />

                <Button
                  className={'mt-1'}
                  icon={IconDelete}
                  onClick={(): void => {
                    handleRemove(key);
                  }}
                  size={'sm'}
                  type={'button'}
                  variant={'outline-secondary'}
                >
                  {t('taskTemplateModal|remove')}
                </Button>
              </div>
            </div>
          );
        })}
        {!show && (
          <Button
            className="rounded"
            icon={IconCreate}
            onClick={(): void => setShow(true)}
            size={'sm'}
            style={{ width: '150px', height: '150px' }}
            variant={'outline-secondary'}
          >
            {t('taskTemplateModal|addImageButtonLabel')}
          </Button>
        )}
        {busy && <ImageUploading />}
        {lightBoxImageUrl ? (
          <LightBoxModal
            imageUrl={lightBoxImageUrl}
            onClose={(): void => setLightBoxImageUrl('')}
          />
        ) : null}
      </div>
    </div>
  );
}
